import { Component } from "react";
import { API } from "@/lib/api";

// Global error boundary so a runtime error never leaves a blank white page.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Unexpected error" };
  }

  componentDidCatch(error, info) {
    // Surface the error for debugging (visible in browser console / logs).
    console.error("[ErrorBoundary]", error, info?.componentStack);
    const message = error?.message || String(error);
    // Skip non-actionable noise: (1) pages opened as local files (origin 'null')
    // throw history.replaceState errors; (2) stale/missing JS chunks after a deploy
    // are transient and already auto-recovered by lazyRetry (reload once).
    const fileOrigin = window.location.protocol === "file:" || window.location.origin === "null";
    const historyNoise = /(replaceState|pushState)/.test(message) && /file:\/\//.test(message);
    const chunkNoise = /Loading (CSS )?chunk|ChunkLoadError|dynamically imported module/i.test(message);
    if (fileOrigin || historyNoise || chunkNoise) return;
    // Report to backend so real user crashes (e.g. on specific WiFi networks) can be diagnosed.
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      fetch(`${API}/errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          message,
          stack: error?.stack || "",
          component_stack: info?.componentStack || "",
          url: window.location.href,
          user_agent: navigator.userAgent,
          connection: conn ? { type: conn.type, effectiveType: conn.effectiveType, downlink: conn.downlink, rtt: conn.rtt } : null,
        }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }

  handleReload = () => {
    this.setState({ hasError: false, message: "" });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="error-boundary">
        <h1 className="font-head text-2xl font-700">Something went wrong / Coś poszło nie tak</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          The page hit an unexpected error. Please reload and try again.
          <br />
          Strona napotkała nieoczekiwany błąd. Odśwież i spróbuj ponownie.
        </p>
        <button
          data-testid="error-reload-btn"
          onClick={this.handleReload}
          className="rounded-full bg-primary px-6 py-3 text-sm font-700 text-primary-foreground transition-transform hover:scale-105"
        >
          Reload / Odśwież
        </button>
      </div>
    );
  }
}
